import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Phone, User, Mail, Sparkles, PartyPopper, Loader2, X, LogIn, MessageCircle, CheckCircle, Instagram, Facebook, Youtube } from "lucide-react";
import { API_URL, WHATSAPP_LINK, LOGO_URL, axios } from "../../constants";
import { logger } from "../../utils/logger";

export const LeadCapturePopup = ({ onCapture }) => {
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Persist dismissal for 14 days so the popup stops re-opening and blocking
  // the top nav after a user has already said "not now".
  const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000;
  const dismissPopup = () => {
    try {
      localStorage.setItem('doozygo_popup_skipped', 'true');
      localStorage.setItem('doozygo_popup_skipped_at', String(Date.now()));
    } catch { /* storage disabled — session-only dismiss is fine */ }
    setShow(false);
  };

  useEffect(() => {
    const stored = localStorage.getItem('doozygo_customer');
    if (stored) { onCapture(JSON.parse(stored)); return; }
    const skippedAt = parseInt(localStorage.getItem('doozygo_popup_skipped_at') || '0', 10);
    const skipped = localStorage.getItem('doozygo_popup_skipped');
    const stillSuppressed = skippedAt && (Date.now() - skippedAt) < DISMISS_TTL_MS;
    // Legacy skips without timestamp are treated as permanent until the user clears storage.
    if (skipped && (stillSuppressed || !skippedAt)) return;

    // Never pop over the booking form — users who scrolled/jumped there are
    // mid-transaction and the modal covers the calendar + date cells, which
    // is exactly how the "cannot select a date" bug was reproduced (iter184).
    const isAtBooking = () => {
      if (typeof window === 'undefined') return false;
      if (window.location.hash === '#book-online') return true;
      const el = document.getElementById('book-online');
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    };
    if (isAtBooking()) return;

    // Longer delay (8s) so first-time visitors have a chance to see the hero
    // and click the top nav before this modal takes over.
    const timer = setTimeout(() => {
      // Re-check at fire-time: if the user has scrolled to the booking
      // section during the 8-second window, bail out silently.
      if (isAtBooking()) return;
      setShow(true);
    }, 8000);
    return () => clearTimeout(timer);
  }, [onCapture, DISMISS_TTL_MS]);

  // If the popup is already open and the user scrolls into the booking
  // section (or starts interacting with it), auto-dismiss so the calendar
  // becomes clickable without a manual close.
  useEffect(() => {
    if (!show) return;
    const check = () => {
      const el = document.getElementById('book-online');
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.6 && rect.bottom > 0) dismissPopup();
    };
    window.addEventListener('scroll', check, { passive: true });
    check();
    return () => window.removeEventListener('scroll', check);
  }, [show]);

  // Dismiss on Escape key — standard modal UX.
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => { if (e.key === 'Escape') dismissPopup(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim()) { setError('Name and phone are required'); return; }
    if (formData.phone.length < 10) { setError('Please enter a valid phone number'); return; }
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API_URL}/api/leads`, formData);
      localStorage.setItem('doozygo_customer', JSON.stringify(res.data));
      onCapture(res.data); setShow(false);
    } catch (err) { setError('Something went wrong. Please try again.'); }
    setLoading(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      data-testid="lead-capture-popup"
      onClick={dismissPopup}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={dismissPopup}
          aria-label="Close"
          data-testid="lead-popup-close"
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/90 text-[#1E1B4B] hover:bg-white hover:scale-105 transition shadow-md"
        >
          <X size={16} />
        </button>
        <div className="bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] p-6 text-center">
          <img src={LOGO_URL} alt="DoozyGo" className="h-16 mx-auto mb-2" />
          <h2 className="text-xl font-bold text-white font-['Outfit']">Welcome to DoozyGo!</h2>
          <p className="text-white/80 text-sm mt-1">Tell us about yourself and get exclusive party offers</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#1E1B4B] mb-1.5 flex items-center gap-1.5"><User size={14} /> Your Name *</label>
            <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Enter your name" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" data-testid="lead-name-input" autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1E1B4B] mb-1.5 flex items-center gap-1.5"><Phone size={14} /> Phone Number *</label>
            <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10)})} placeholder="10-digit phone number" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" data-testid="lead-phone-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1E1B4B] mb-1.5 flex items-center gap-1.5"><Mail size={14} /> Email (Optional)</label>
            <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="your@email.com" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm" data-testid="lead-email-input" />
          </div>
          {error && <p className="text-red-500 text-xs">{error}</p>}
          <button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white py-3 rounded-xl font-bold text-sm transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="lead-submit-btn">
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
            {loading ? 'Saving...' : 'Get Party Offers'}
          </button>
          <button type="button" onClick={dismissPopup} data-testid="lead-skip-btn" className="w-full text-[#94a3b8] text-xs py-1 hover:text-[#475569] transition-colors">Skip for now</button>
        </form>
      </div>
    </div>
  );
};

export const OfferBanner = ({ onHeight }) => {
  const [offers, setOffers] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const bannerRef = useRef(null);

  useEffect(() => {
    const fetchOffers = async () => {
      try { const res = await axios.get(`${API_URL}/api/offers/active`); setOffers(res.data || []); }
      catch (err) { logger.error(err); }
    };
    fetchOffers();
  }, []);

  useEffect(() => {
    if (onHeight) onHeight(dismissed || offers.length === 0 ? 0 : bannerRef.current?.offsetHeight || 0);
  }, [dismissed, offers, onHeight]);

  if (dismissed || offers.length === 0) return null;
  const offer = offers[0];

  return (
    <div ref={bannerRef} className="fixed top-0 left-0 right-0 bg-gradient-to-r from-[#E83E8C] via-[#FF9F1C] to-[#FFD166] text-white py-2.5 px-4 z-[60]" data-testid="offer-banner">
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm">
        <Sparkles size={16} className="flex-shrink-0 animate-pulse" />
        <span className="font-semibold">{offer.title}</span>
        {offer.discount_code && (<span className="bg-white/20 px-2.5 py-0.5 rounded-full text-xs font-bold tracking-wider">Code: {offer.discount_code}</span>)}
        <span className="hidden sm:inline text-white/90">{offer.description}</span>
        <button onClick={() => setDismissed(true)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"><X size={16} /></button>
      </div>
    </div>
  );
};

export const SocialReferralSection = () => {
  const [showForm, setShowForm] = useState(false);
  const [phone, setPhone] = useState(""); const [name, setName] = useState("");
  const [platform, setPlatform] = useState("instagram"); const [postUrl, setPostUrl] = useState("");
  const [submitting, setSubmitting] = useState(false); const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); if (!phone || !name || !postUrl) return;
    setSubmitting(true);
    try { await axios.post(`${API_URL}/api/social/submit`, { phone: phone.trim(), name: name.trim(), platform, post_url: postUrl.trim() }); setSubmitted(true); }
    catch (err) { alert(err.response?.data?.detail || "Something went wrong"); }
    setSubmitting(false);
  };

  return (
    <section className="py-20 md:py-28 bg-gradient-to-br from-[#1E1B4B] to-[#312e81] relative overflow-hidden" data-testid="social-referral-section">
      <div className="absolute top-10 left-10 w-32 h-32 bg-[#E83E8C]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-10 right-10 w-40 h-40 bg-[#FF9F1C]/10 rounded-full blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-[#FF9F1C]/20 text-[#FF9F1C] rounded-full text-sm font-bold tracking-wider mb-4">TAG & EARN</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] mb-4">Share the Party Love, Get Rewarded!</h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">Post your DoozyGo party photos on Instagram or Facebook, tag us, and earn exclusive discounts & freebies!</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {[
            { posts: "1 Post", reward: "5% Off Next Booking", icon: "🏅", color: "from-[#CD7F32] to-[#A0522D]", tier: "Bronze" },
            { posts: "3+ Posts", reward: "10% Off + Featured on Our Page", icon: "🥈", color: "from-[#C0C0C0] to-[#808080]", tier: "Silver" },
            { posts: "5+ Posts", reward: "Free Add-on Upgrade (₹999 value)", icon: "🥇", color: "from-[#FFD700] to-[#FF9F1C]", tier: "Gold" },
          ].map((t, i) => (
            <div key={t.tier} className={`bg-gradient-to-br ${t.color} rounded-3xl p-6 text-center text-white shadow-xl`}>
              <span className="text-4xl">{t.icon}</span>
              <h3 className="text-xl font-bold mt-3 font-['Outfit']">{t.tier} Tier</h3>
              <p className="text-white/80 text-sm mt-1">{t.posts}</p>
              <p className="text-lg font-bold mt-3">{t.reward}</p>
            </div>
          ))}
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 mb-12">
          <h3 className="text-xl font-bold text-white text-center mb-6 font-['Outfit']">How It Works</h3>
          <div className="grid md:grid-cols-4 gap-6 text-center">
            {[
              { step: "1", title: "Book & Celebrate", desc: "Enjoy your DoozyGo® party" },
              { step: "2", title: "Snap & Post", desc: "Share photos on Instagram/Facebook" },
              { step: "3", title: "Tag @DoozyGo®", desc: "Tag us in your post" },
              { step: "4", title: "Earn Rewards!", desc: "Submit your link & get rewarded" },
            ].map((s, i) => (
              <div key={s.step}>
                <div className="w-10 h-10 bg-gradient-to-br from-[#E83E8C] to-[#FF9F1C] rounded-full flex items-center justify-center text-white font-bold mx-auto mb-3">{s.step}</div>
                <h4 className="text-white font-bold text-sm">{s.title}</h4>
                <p className="text-white/60 text-xs mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="text-center">
          {!showForm && !submitted && (
            <button onClick={() => setShowForm(true)} className="px-8 py-4 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white rounded-2xl font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all" data-testid="social-submit-btn">Submit Your Post & Claim Reward</button>
          )}
          {showForm && !submitted && (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto bg-white rounded-3xl p-6 shadow-2xl text-left" data-testid="social-submit-form">
              <h3 className="text-lg font-bold text-[#1E1B4B] mb-4 font-['Outfit']">Submit Your Social Post</h3>
              <div className="space-y-3">
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Your Name" required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#E83E8C] outline-none" data-testid="social-name" />
                <input type="tel" inputMode="numeric" maxLength={10} value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit phone number" required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#E83E8C] outline-none" data-testid="social-phone" />
                <div className="flex gap-2">
                  {["instagram", "facebook"].map(p => (
                    <button key={p} type="button" onClick={() => setPlatform(p)} className={`flex-1 py-2 rounded-xl text-sm font-medium capitalize transition-all ${platform === p ? 'bg-[#E83E8C] text-white' : 'bg-gray-100 text-gray-500'}`} data-testid={`social-platform-${p}`}>{p}</button>
                  ))}
                </div>
                <input value={postUrl} onChange={e => setPostUrl(e.target.value)} placeholder="Paste your post link here" required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#E83E8C] outline-none" data-testid="social-url" />
                <button type="submit" disabled={submitting} className="w-full py-3 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white rounded-xl font-bold text-sm disabled:opacity-50" data-testid="social-submit-form-btn">{submitting ? "Submitting..." : "Submit for Review"}</button>
              </div>
            </form>
          )}
          {submitted && (
            <div className="bg-white rounded-3xl p-8 max-w-md mx-auto shadow-2xl text-center" data-testid="social-submit-success">
              <CheckCircle size={48} className="mx-auto text-[#25D366] mb-4" />
              <h3 className="text-xl font-bold text-[#1E1B4B] font-['Outfit']">Post Submitted!</h3>
              <p className="text-sm text-[#475569] mt-2">We'll verify your post and send your reward code via WhatsApp within 24 hours.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  return (
    <footer className="relative bg-[#1E1B4B] overflow-hidden" data-testid="footer">
      {/* Top gradient accent line */}
      <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#E83E8C] to-transparent opacity-60" />

      {/* Decorative blurred orbs */}
      <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#E83E8C]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-[#7C3AED]/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-12 pb-6">
        {/* Main grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Brand column */}
          <div className="lg:col-span-4">
            <div className="inline-flex bg-white rounded-xl px-4 py-2 shadow-[0_8px_24px_rgba(232,62,140,0.15)]">
              <img src={LOGO_URL} alt="DoozyGo" className="h-10 w-auto" />
            </div>
            <p className="text-indigo-200/70 text-sm leading-relaxed mt-4 max-w-xs">
              Premium house party setups across Mumbai, Thane &amp; Navi Mumbai.
              <br />
              We bring the celebration to your doorstep.
            </p>
            <div className="mt-5 flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-semibold uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live in Mumbai
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FFD166]/10 border border-[#FFD166]/30 text-[#FFD166] text-[10px] font-semibold uppercase tracking-wider">
                ★ 4.9 / 5
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-3">
            <h4 className="text-white text-[11px] font-bold uppercase tracking-[0.18em] mb-4 flex items-center gap-2">
              <span className="w-6 h-px bg-[#E83E8C]" /> Quick Links
            </h4>
            <ul className="space-y-2.5">
              <li><Link to="/my-account" className="text-indigo-200/70 hover:text-[#E83E8C] hover:translate-x-0.5 transition-all text-sm inline-block">My Account</Link></li>
              <li><Link to="/track-booking" className="text-indigo-200/70 hover:text-[#E83E8C] hover:translate-x-0.5 transition-all text-sm inline-block">Track Booking</Link></li>
              <li><Link to="/cancel-booking" className="text-indigo-200/70 hover:text-[#E83E8C] hover:translate-x-0.5 transition-all text-sm inline-block">Cancel Booking</Link></li>
              <li><Link to="/privacy-policy" className="text-indigo-200/70 hover:text-[#E83E8C] hover:translate-x-0.5 transition-all text-sm inline-block">Privacy Policy</Link></li>
              <li><Link to="/terms-of-service" className="text-indigo-200/70 hover:text-[#E83E8C] hover:translate-x-0.5 transition-all text-sm inline-block">Terms of Service</Link></li>
              <li><Link to="/admin" className="text-indigo-200/40 hover:text-[#E83E8C] transition-colors text-xs inline-flex items-center gap-1.5 mt-1"><LogIn size={11} /> Admin Portal</Link></li>
            </ul>
          </div>

          {/* Explore — second link column */}
          <div className="lg:col-span-2">
            <h4 className="text-white text-[11px] font-bold uppercase tracking-[0.18em] mb-4 flex items-center gap-2">
              <span className="w-6 h-px bg-[#FF9F1C]" /> Explore
            </h4>
            <ul className="space-y-2.5">
              <li><Link to="/doozygo-magic" className="text-indigo-200/70 hover:text-[#E83E8C] hover:translate-x-0.5 transition-all text-sm inline-block">DoozyGo Magic</Link></li>
              <li><Link to="/creators" className="text-indigo-200/70 hover:text-[#E83E8C] hover:translate-x-0.5 transition-all text-sm inline-flex items-center gap-1.5"><Sparkles size={12} className="text-[#FF9F1C]" /> Creator Studio</Link></li>
              <li><Link to="/vendors" className="text-indigo-200/70 hover:text-[#E83E8C] hover:translate-x-0.5 transition-all text-sm inline-block">Vendor Marketplace</Link></li>
              <li><Link to="/vendor-register" className="text-indigo-200/70 hover:text-[#E83E8C] hover:translate-x-0.5 transition-all text-sm inline-block">Become a Partner</Link></li>
              {/*
                Vendor Portal link — permanent, do NOT remove without founder sign-off.
                Partners asked for this on the homepage footer (iter167) so they can
                log back in to their dashboard; without this, they had to type the URL
                manually or lose their way through the registration flow. The sibling
                test in /app/frontend/src/__tests__/footer-vendor-portal.test.js guards
                against accidental deletion.
              */}
              <li><Link to="/vendor-portal" data-testid="footer-vendor-portal-link" className="text-indigo-200/70 hover:text-[#E83E8C] hover:translate-x-0.5 transition-all text-sm inline-block">Vendor Portal</Link></li>
            </ul>
          </div>

          {/* Connect column */}
          <div className="lg:col-span-3">
            <h4 className="text-white text-[11px] font-bold uppercase tracking-[0.18em] mb-4 flex items-center gap-2">
              <span className="w-6 h-px bg-[#7C3AED]" /> Connect With Us
            </h4>
            <div className="flex items-center gap-2.5 mb-5">
              <a href="https://www.instagram.com/doozygo/" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-indigo-200/80 hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#E83E8C] hover:to-[#FF9F1C] hover:text-white hover:border-transparent hover:scale-110 transition-all duration-200"
                data-testid="social-instagram" aria-label="Instagram">
                <Instagram size={15} strokeWidth={1.6} />
              </a>
              <a href="https://www.facebook.com/doozygo" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-indigo-200/80 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] hover:scale-110 transition-all duration-200"
                data-testid="social-facebook" aria-label="Facebook">
                <Facebook size={15} strokeWidth={1.6} />
              </a>
              <a href="https://wa.me/919930001848?text=Hi%20DoozyGo!" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-indigo-200/80 hover:bg-[#25D366] hover:text-white hover:border-[#25D366] hover:scale-110 transition-all duration-200"
                data-testid="social-whatsapp" aria-label="WhatsApp">
                <MessageCircle size={15} strokeWidth={1.6} />
              </a>
              <a href="https://www.youtube.com/@doozygo" target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-indigo-200/80 hover:bg-[#FF0000] hover:text-white hover:border-[#FF0000] hover:scale-110 transition-all duration-200"
                data-testid="social-youtube" aria-label="YouTube">
                <Youtube size={15} strokeWidth={1.6} />
              </a>
            </div>
            <div className="space-y-2.5">
              <a href="https://wa.me/919930001848?text=Hi%20DoozyGo!" target="_blank" rel="noopener noreferrer"
                className="group flex items-center gap-2.5 text-indigo-200/80 hover:text-white transition-colors text-sm">
                <span className="w-8 h-8 rounded-lg bg-[#25D366]/15 border border-[#25D366]/25 flex items-center justify-center group-hover:bg-[#25D366] group-hover:border-[#25D366] transition-colors">
                  <MessageCircle size={13} className="text-[#25D366] group-hover:text-white" />
                </span>
                Chat on WhatsApp
              </a>
              <a href="mailto:doozygo@gmail.com"
                className="group flex items-center gap-2.5 text-indigo-200/80 hover:text-white transition-colors text-sm">
                <span className="w-8 h-8 rounded-lg bg-[#E83E8C]/15 border border-[#E83E8C]/25 flex items-center justify-center group-hover:bg-[#E83E8C] group-hover:border-[#E83E8C] transition-colors">
                  <Mail size={13} className="text-[#E83E8C] group-hover:text-white" />
                </span>
                doozygo@gmail.com
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/[0.08] mt-10 pt-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-indigo-200/50 text-xs text-center sm:text-left">
            &copy; 2025 DoozyGo® &mdash; A Brand of{" "}
            <a
              href="https://www.bigkreation.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-200/80 hover:text-[#E83E8C] underline underline-offset-2 decoration-dotted decoration-indigo-200/40 hover:decoration-[#E83E8C] transition-colors font-medium"
              data-testid="footer-bigkreation-link"
            >
              Big Kreation Entertainment Pvt Ltd.
            </a>
          </p>
          <p className="text-indigo-200/40 text-[11px] flex items-center gap-1.5">
            Made with <span className="text-[#E83E8C]">&hearts;</span> in Mumbai
          </p>
        </div>
      </div>
    </footer>
  );
};

export const FloatingWhatsApp = () => {
  return (
    <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 whatsapp-pulse" data-testid="floating-whatsapp">
      <div className="flex items-center gap-2 bg-[#25D366] text-white pl-4 pr-5 py-3 rounded-full font-semibold shadow-lg hover:bg-[#1da851] transition-all hover:scale-105">
        <MessageCircle size={22} />
      </div>
    </a>
  );
};

export const CookieConsent = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem('doozygo_cookies_accepted');
    if (!accepted) {
      const timer = setTimeout(() => setShow(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('doozygo_cookies_accepted', 'true');
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[90] animate-fade-in-up" data-testid="cookie-consent">
      <div className="bg-[#1E1B4B] border-t border-white/10 px-4 py-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-white/90 text-sm">
            <img src={LOGO_URL} alt="DoozyGo" className="h-8 bg-white rounded-lg px-1.5 py-0.5 flex-shrink-0" />
            <p>We use cookies to enhance your browsing experience and analyze site traffic. By continuing to use our site, you agree to our <a href="/privacy-policy" className="text-[#FF9F1C] underline hover:text-[#FFD166]">Privacy Policy</a>.</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <button onClick={handleAccept} className="bg-[#E83E8C] hover:bg-[#d62f7a] text-white px-6 py-2 rounded-full text-sm font-semibold transition-all hover:scale-105" data-testid="cookie-accept-btn">Accept All</button>
            <button onClick={handleAccept} className="text-white/60 hover:text-white text-sm transition-colors" data-testid="cookie-dismiss-btn">Dismiss</button>
          </div>
        </div>
      </div>
    </div>
  );
};
